import {BuyDataLoadedEvent, BuyDataLoadedResponse} from '../Interfaces/BuyDataLoaded';

interface LeasingThresholds {
    thresholdAmount: number,
    periods: [number],
    default: number,
}

export class Leasing {
    protected element: HTMLElement;
    protected placeholderEl: HTMLElement;
    protected resultsEl: HTMLElement;
    protected errorEl: HTMLElement;
    protected valueEl: HTMLElement;
    protected monthsListEl: HTMLElement;

    constructor(element: HTMLElement) {
        this.element = element;

        this.placeholderEl = element.querySelector('.nwd-leasing-placeholder');
        this.resultsEl = element.querySelector('.nwd-leasing-results');
        this.errorEl = element.querySelector('.nwd-leasing-error');
        this.valueEl = element.querySelector('.nwd-leasing-value');
        this.monthsListEl = element.querySelector('.nwd-leasing-months-list');

        element.addEventListener('Plandent.Buy.DataLoaded', this.dataLoadedHandler, {passive: true});
    }

    /**
     * Handler function if the data was loaded
     *
     * @param e
     */
    protected dataLoadedHandler = (e: BuyDataLoadedEvent) => {
        const price = e.detail.price?.amount;

        if (price) {
            const leasing = this.element.dataset.leasing
                ? JSON.parse(this.element.dataset.leasing)
                : [];

            let defaultPeriod = 0;
            let periods = [];

            leasing.forEach((threshold: LeasingThresholds) => {
                if (price >= threshold.thresholdAmount) {
                    defaultPeriod = threshold.default;
                    periods = threshold.periods;
                }
            });

            try {
                const response = this.leasingRequest(price, defaultPeriod);
                response
                    .then((data: BuyDataLoadedResponse) => {
                        if (data?.error) {
                            throw new Error(data.error);
                        }

                        this.element.dataset.items = JSON.stringify(data.data.Items);
                        this.element.dataset.message = data.message;
                        this.element.dataset.monthsMessage = data.monthsMessage;

                        this.updateMessage(periods, defaultPeriod);
                    })
                    .catch((e) => {
                        this.placeholderEl.style.display = 'none';
                        this.resultsEl.style.display = 'none';
                        this.errorEl.innerHTML = e.message;
                        this.errorEl.style.display = 'block';
                    });
            } catch (e) {
                console.error(e);
                this.placeholderEl.style.display = 'none';
                this.resultsEl.style.display = 'none';
                this.errorEl.innerHTML = e.message;
                this.errorEl.style.display = 'block';
            }
        }
    }

    /**
     * Api request for the leasing values
     *
     * @param price
     * @param months
     */
    protected leasingRequest = async (price: number, months: number) => {
        const uri = new URL('/leasing', window.location.href);
        uri.searchParams.set('price', price?.toString());
        uri.searchParams.set('months', months?.toString());

        const response = await fetch(uri, {
            headers: {
                "Content-Type": "application/json",
            },
        });

        return response.json();
    }

    /**
     * update the frontend messages
     *
     * @param periods
     * @param defaultPeriod
     */
    protected updateMessage = (periods: any[], defaultPeriod: number) => {
        const dataItems = JSON.parse(this.element.dataset.items);
        const dataMessage = this.element.dataset.message
        const dataMonthsMessage = this.element.dataset.monthsMessage;
        const dataMonths = this.element.dataset.months ?? defaultPeriod;

        const data = dataItems.find((i) => i.Period == dataMonths);

        const numberFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
        const monthlyTotalInstalment = numberFormat.format(data.MonthlyTotalInstalment);

        const message = dataMessage
            .replace(':price', `<strong>${monthlyTotalInstalment}</strong>`)
            .replace(':months', `<strong>${data.Period}</strong>`);

        this.placeholderEl.style.display = 'none';
        this.errorEl.style.display = 'none';

        this.valueEl.innerHTML = message;
        this.resultsEl.style.display = 'block';

        const list = dataItems.map((i) => {
            // @ts-ignore
            if (!periods.includes(i.Period)) {
                return;
            }

            return `
                <li class="${i.Period == data.Period ? 'nwd-leasing-selected' : ''}">
                    <a href="#" data-period="${i.Period}">${dataMonthsMessage.replace(':months', i.Period)}</a>
                 </li>
            `
        });

        this.monthsListEl.innerHTML = list.join('');

        const links = this.monthsListEl.querySelectorAll('li > a');
        links.forEach((link: HTMLLinkElement) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();

                this.element.dataset.months = link.dataset.period;
                this.updateMessage(periods, defaultPeriod);
            }, {passive: false})
        });
    }

    /**
     * Create Leasing-Instances from a NodeList of HTMLSelectElements or a selector string
     *
     * @param {NodeListOf<HTMLSelectElement>|string} elementList
     */
    static createInstances = (elementList:NodeListOf<HTMLSelectElement>|string) => {
        if (typeof elementList === 'string') {
            elementList = document.querySelectorAll(elementList);
        }

        if (elementList.length > 0) {
            Array.from(elementList).forEach((el) => {
                new Leasing(el);
            });
        }
    }
}

/**
 * Initialize Leasing
 */
export function initLeasing() {
    Leasing.createInstances('.nwd-leasing');
}
